/**
 * Decide se — e como — o card de largura total se alinha à coluna de leitura
 * da página (RF03).
 *
 * O card é renderizado pelo `ContentContainer`, irmão do bloco de conteúdo,
 * enquanto o grid de 12 colunas (`.mpf-internal-view__grid`) só existe dentro
 * de `{children}`, montado pelo `InternalLayout`. Não dá para herdar o grid:
 * este helper diz qual classe de wrapper reproduzi-lo.
 *
 * As regras abaixo espelham a resolução de view do projeto — se aquela mudar,
 * esta precisa acompanhar:
 * - `Document` -> `InternalView` e `Folder` -> `FolderView` usam o grid;
 * - `Collection` -> `AllNews` e `Event` -> `EventView` do core (Semantic UI,
 *   outro sistema de larguras) não usam. Alinhar ali seria alinhar a uma
 *   coluna que a página não tem;
 * - `FolderView` desvia para `AllNews` quando algum filho é `Collection`;
 * - a coluna de leitura só é centralizada quando não há "Links úteis" — é o
 *   mesmo sinal de `useHasSidebar` em `components/Layouts/index.tsx`;
 * - por cima de tudo isso vem o campo `largura_total` da página (aba
 *   Configurações), que é escolha explícita do editor — o espelho de
 *   `--full` em `components/Layouts/contentAreaClassName.ts`.
 */

const READING_COLUMN_TYPES = ['Document', 'Folder'];

const ALIGN = 'mpf-unidade-responsavel-align';

interface AlignmentContent {
  '@type'?: string;
  items?: { '@type'?: string }[];
  relatedItems?: unknown[];
  largura_total?: boolean;
}

export const getAlignmentClassName = (
  content?: AlignmentContent | null,
): string | null => {
  if (!READING_COLUMN_TYPES.includes(content?.['@type'] as string)) {
    return null;
  }

  const hasCollectionChild = (content?.items || []).some(
    (item) => item['@type'] === 'Collection',
  );
  if (hasCollectionChild) {
    return null;
  }

  const hasRelatedItems =
    Array.isArray(content?.relatedItems) && content.relatedItems.length > 0;

  // Com "Links úteis" — ou com a chave "Página em largura total" ligada — o
  // conteúdo ocupa as 12 colunas; sem nenhum dos dois, fica em 8/12
  // centralizado. O card acompanha os dois casos.
  const larguraTotal = content?.largura_total === true;

  return hasRelatedItems || larguraTotal ? `${ALIGN} ${ALIGN}--wide` : ALIGN;
};

export default getAlignmentClassName;
